import { isSameSelectionSide } from './functions.tsx';
import { Odd } from '../models/Odd_response_model.tsx';

type ParlayRefreshBet = {
  id_selection?: string;
  betName?: string;
  marketName?: string;
  odd?: Odd | null;
  odds?: Odd[];
};

/**
 * Prefer the exact selected odd among a refreshed odds list so the price UI
 * and showParlays payload stay in sync.
 */
export function pickMatchingParlayOdd(
  freshOdds: Odd[],
  bet: ParlayRefreshBet,
): Odd | undefined {
  if (!freshOdds.length) return undefined;

  const byId = freshOdds.find(
    o => o.id === bet.id_selection || o.id === bet.odd?.id,
  );
  if (byId) return byId;

  const byName = freshOdds.find(
    o => o.name === bet.odd?.name || o.name === bet.betName,
  );
  if (byName) return byName;

  if (bet.odd && (bet.odd.player_id || bet.odd.team_id || bet.odd.selection_line)) {
    const bySide = freshOdds.find(o => {
      if (!isSameSelectionSide(bet.odd!, o)) return false;
      if (bet.odd?.points != null && o.points != null) {
        return Number(o.points) === Number(bet.odd.points);
      }
      return true;
    });
    if (bySide) return bySide;
  }

  if (bet.odd?.team_id) {
    const byTeam = freshOdds.find(o => o.team_id === bet.odd?.team_id && o.is_main);
    if (byTeam) return byTeam;
    const anyTeam = freshOdds.find(o => o.team_id === bet.odd?.team_id);
    if (anyTeam) return anyTeam;
  }

  return freshOdds.find(o => o.is_main) ?? freshOdds[0];
}

export function applyRefreshedParlayOdds<T extends ParlayRefreshBet>(
  bet: T,
  freshOdds: Odd[],
): T {
  if (!freshOdds.length) return bet;
  const matched = pickMatchingParlayOdd(freshOdds, bet);
  if (!matched) {
    return { ...bet, odds: freshOdds };
  }

  return {
    ...bet,
    odds: freshOdds,
    odd: matched,
    id_selection: matched.id,
    betName: matched.name,
    marketName: matched.market,
  };
}
